import { defineStore } from 'pinia';
import conn from '@/services/connector.service';

export const useCoursesStore = defineStore('courses', {
  state: () => ({
    enrollments: [],
    certificatesAndMastery: [],
    collegeCredits: {
      creditsCompleted: 0,
      creditsInProgress: 3,
      creditsPending: 0
    },
    showLearnerProgress: false,
    estimatedSavings: {
      creditsCompleted: 0,
      totalCost: 0,
      estimatedCost: { min: 0, max: 0 }
    },
    degreeProgress: {
      completedCredits: 0,
      inProgressCredits: 0,
      pendingCredits: 0,
      totalCredits: 60
    },
    learnerUGApplicationStatus: '',
    gpaCourses: [],
    paypalTransactionId: '',
    paypalCardId: '',
    enrolledCourses: [],
    selectedCourses: [], // For AddCourses.vue course selection
    globalCourseSections: [], // For CourseHistory.vue course sections
    // ACC-439: queued OD preregistrations, projected into upcoming-course shape. Kept
    // beside the sections rather than inside them because processEnrollments rebuilds
    // globalCourseSections from /api/enrollments on every navigation, and these come
    // from a second source that must survive that rebuild.
    preregisteredCourses: [],
    semesterYearProgress: [],
    // V2 API data - single object to store everything
    learnerCourses: null
  }),

  actions: {
    /**
     * Fetch courses for learner using V2 API endpoint
     * Only fetches if data is not already loaded
     * Uses connector service for all API/auth logic
     * @param {string} learnerId - EA Learner Application ID
     * @param {boolean} forceRefresh - Force refresh even if data exists
     * @returns {Promise<Object>} Response with courses, filterOptions, summary
     */
    async fetchCoursesForLearner(learnerId, forceRefresh = true) {
      // TODO: forceRefresh is true, until we implement state refresh once all Pages use the courses store.

      // Return cached data if already initialized and not forcing refresh
      if (this.learnerCourses && !forceRefresh) return this.learnerCourses;

      try {
        // Use connector service - it handles all auth, headers, error handling
        const response = await conn.getCoursesForLearnerV2(learnerId);

        // Store entire response in single state object
        this.learnerCourses = response;

        return response;
      } catch (error) {
        console.error('Failed to fetch courses for learner:', error);
        throw error;
      }
    },
    setEnrollments(payload) {
      this.enrollments = payload;
    },
    setCertificatesAndMastery(payload) {
      this.certificatesAndMastery = payload;
    },
    setCollegeCredits(payload) {
      this.collegeCredits = payload;
    },
    setShowLearnerProgress(payload) {
      this.showLearnerProgress = payload;
    },
    setEstimatedSavings(payload) {
      this.estimatedSavings = payload;
    },
    setDegreeProgress(payload) {
      this.degreeProgress = payload;
    },
    setLearnerUGApplicationStatus(payload) {
      this.learnerUGApplicationStatus = payload;
    },
    setGPACourses(payload) {
      this.gpaCourses = payload;
    },
    setEnrolledCourses(payload) {
      this.enrolledCourses = payload;
    },
    setSelectedCourses(payload) {
      this.selectedCourses = payload;
    },
    setGlobalCourseSections(payload) {
      this.globalCourseSections = payload;
    },
    setPreregisteredCourses(payload) {
      this.preregisteredCourses = payload || [];
    },
    setPaypalTransactionId(payload) {
      this.paypalTransactionId = payload;
    },
    setPaypalCardId(payload) {
      this.paypalCardId = payload;
    },
    setSemesterYearProgress(payload) {
      this.semesterYearProgress = payload;
    }
  },

  getters: {
    // Legacy getters kept for backward compatibility
    getEnrollments: (state) => state.enrollments,
    getCertificatesAndMastery: (state) => state.certificatesAndMastery,
    getCollegeCredits: (state) => state.collegeCredits,
    getDegreeProgress: (state) => state.degreeProgress,
    getSemesterYearProgress: (state) => state.semesterYearProgress
  }
});
